/**
 * StylePanel — F12.B T12.B.6
 *
 * Prawa zakładka "Styl" w ModuleBuilderPage dla zaznaczonego elementu.
 * Pozwala na customizację CSS per element w layout_config.
 *
 * Sekcje MVP:
 *  - Typografia (rozmiar, grubość, kolor, wyrównanie, dekoracja)
 *  - Wypełnienie (kolor tła, przezroczystość)
 *  - Obramowanie (grubość, kolor, promień, styl)
 *  - Odstępy (padding per strona)
 *  - Efekty (przezroczystość elementu)
 */

import { useState } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ChevronDown,
  Paintbrush,
  Ruler,
  Sparkles,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';

import type { LayoutElementStyle } from './style/styleTypes';

// ─── Props ───────────────────────────────────────────────────────────────────

interface StylePanelProps {
  style: LayoutElementStyle | undefined;
  onChange: (style: LayoutElementStyle) => void;
}

// ─── Section IDs ─────────────────────────────────────────────────────────────

type SectionId = 'typography' | 'fill' | 'border' | 'spacing' | 'effects';

const SECTIONS: Array<{ id: SectionId; label: string; icon: React.ElementType }> = [
  { id: 'typography', label: 'Typografia', icon: Type },
  { id: 'fill', label: 'Wypełnienie', icon: Paintbrush },
  { id: 'border', label: 'Obramowanie', icon: Ruler },
  { id: 'spacing', label: 'Odstępy', icon: AlignLeft },
  { id: 'effects', label: 'Efekty', icon: Sparkles },
];

// ─── Small helpers ────────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
      {children}
    </label>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value ?? '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-7 rounded border border-gray-300 dark:border-gray-600 cursor-pointer p-0.5"
        />
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder="#000000"
          className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-[10px] text-gray-400 hover:text-gray-600"
            title="Wyczyść"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

function NumberRow({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit = 'px',
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}) {
  return (
    <div>
      <Label>
        {label} {unit && <span className="text-gray-300">{unit}</span>}
      </Label>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          value={value ?? ''}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            onChange(isNaN(v) ? undefined : v);
          }}
          className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
        {value !== undefined && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-[10px] text-gray-400 hover:text-gray-600"
            title="Wyczyść"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Section renderers ────────────────────────────────────────────────────────

function TypographySection({
  style,
  onChange,
}: {
  style: LayoutElementStyle;
  onChange: (p: Partial<LayoutElementStyle>) => void;
}) {
  const btnCls = (active: boolean) =>
    `flex-1 py-1.5 flex items-center justify-center rounded border text-xs transition-colors ${
      active
        ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/30 text-accent-600'
        : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
    }`;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <NumberRow
          label="Rozmiar"
          value={style.fontSize}
          onChange={(v) => onChange({ fontSize: v })}
          min={8}
          max={120}
        />
        <div>
          <Label>Grubość</Label>
          <Dropdown
            value={String(style.fontWeight ?? '')}
            onChange={(v) => onChange({ fontWeight: v ? Number(v) : undefined })}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            options={[
              { value: '', label: 'Domyślna' },
              ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => ({
                value: String(w),
                label: String(w),
              })),
            ]}
          />
        </div>
      </div>

      <div>
        <Label>Wyrównanie</Label>
        <div className="flex gap-1">
          {[
            { v: 'left' as const, Icon: AlignLeft },
            { v: 'center' as const, Icon: AlignCenter },
            { v: 'right' as const, Icon: AlignRight },
            { v: 'justify' as const, Icon: AlignJustify },
          ].map(({ v, Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange({ textAlign: v })}
              className={btnCls((style.textAlign ?? 'left') === v)}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Dekoracja</Label>
        <div className="flex gap-1">
          {[
            { v: 'none' as const, Icon: Type, label: 'Brak' },
            { v: 'underline' as const, Icon: Underline, label: 'Podkreślenie' },
            { v: 'line-through' as const, Icon: Strikethrough, label: 'Przekreślenie' },
          ].map(({ v, Icon, label }) => (
            <button
              key={v}
              type="button"
              title={label}
              onClick={() => onChange({ textDecoration: v })}
              className={btnCls((style.textDecoration ?? 'none') === v)}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Wielkość liter</Label>
        <div className="flex gap-1">
          {[
            { v: 'none' as const, label: 'Aa' },
            { v: 'uppercase' as const, label: 'AA' },
            { v: 'lowercase' as const, label: 'aa' },
            { v: 'capitalize' as const, label: 'Ab' },
          ].map(({ v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange({ textTransform: v })}
              className={`flex-1 py-1.5 rounded border text-xs font-medium transition-colors ${
                (style.textTransform ?? 'none') === v
                  ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/30 text-accent-600'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <ColorRow
        label="Kolor tekstu"
        value={style.textColor}
        onChange={(v) => onChange({ textColor: v })}
      />
    </div>
  );
}

function FillSection({
  style,
  onChange,
}: {
  style: LayoutElementStyle;
  onChange: (p: Partial<LayoutElementStyle>) => void;
}) {
  return (
    <div className="space-y-3">
      <ColorRow
        label="Kolor tła"
        value={style.backgroundColor}
        onChange={(v) => onChange({ backgroundColor: v })}
      />
      {style.backgroundColor && (
        <div>
          <Label>Przezroczystość tła — {style.backgroundOpacity ?? 100}%</Label>
          <input
            type="range"
            min={0}
            max={100}
            value={style.backgroundOpacity ?? 100}
            onChange={(e) => onChange({ backgroundOpacity: Number(e.target.value) })}
            className="w-full accent-accent-500"
          />
        </div>
      )}
    </div>
  );
}

function BorderSection({
  style,
  onChange,
}: {
  style: LayoutElementStyle;
  onChange: (p: Partial<LayoutElementStyle>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <NumberRow
          label="Grubość"
          value={style.borderWidth}
          onChange={(v) => onChange({ borderWidth: v })}
          min={0}
          max={20}
        />
        <NumberRow
          label="Promień"
          value={style.borderRadius}
          onChange={(v) => onChange({ borderRadius: v })}
          min={0}
          max={100}
        />
      </div>
      {style.borderWidth ? (
        <>
          <ColorRow
            label="Kolor obramowania"
            value={style.borderColor}
            onChange={(v) => onChange({ borderColor: v })}
          />
          <div>
            <Label>Styl obramowania</Label>
            <div className="flex gap-1">
              {(['solid', 'dashed', 'dotted', 'double'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onChange({ borderStyle: v })}
                  className={`flex-1 py-1 text-xs rounded border transition-colors ${
                    (style.borderStyle ?? 'solid') === v
                      ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/30 text-accent-600'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {v === 'solid'
                    ? 'Linia'
                    : v === 'dashed'
                      ? 'Kreski'
                      : v === 'dotted'
                        ? 'Kropki'
                        : '2× linia'}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function SpacingSection({
  style,
  onChange,
}: {
  style: LayoutElementStyle;
  onChange: (p: Partial<LayoutElementStyle>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Padding (wewnętrzny margines)</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <NumberRow
            label="Góra"
            value={style.paddingTop}
            onChange={(v) => onChange({ paddingTop: v })}
            min={0}
            max={200}
          />
          <NumberRow
            label="Dół"
            value={style.paddingBottom}
            onChange={(v) => onChange({ paddingBottom: v })}
            min={0}
            max={200}
          />
          <NumberRow
            label="Lewo"
            value={style.paddingLeft}
            onChange={(v) => onChange({ paddingLeft: v })}
            min={0}
            max={200}
          />
          <NumberRow
            label="Prawo"
            value={style.paddingRight}
            onChange={(v) => onChange({ paddingRight: v })}
            min={0}
            max={200}
          />
        </div>
      </div>
      <div>
        <Label>Margin (zewnętrzny margines)</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <NumberRow
            label="Góra"
            value={style.marginTop}
            onChange={(v) => onChange({ marginTop: v })}
            min={0}
            max={200}
          />
          <NumberRow
            label="Dół"
            value={style.marginBottom}
            onChange={(v) => onChange({ marginBottom: v })}
            min={0}
            max={200}
          />
        </div>
      </div>
    </div>
  );
}

function EffectsSection({
  style,
  onChange,
}: {
  style: LayoutElementStyle;
  onChange: (p: Partial<LayoutElementStyle>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Przezroczystość elementu — {style.opacity ?? 100}%</Label>
        <input
          type="range"
          min={0}
          max={100}
          value={style.opacity ?? 100}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange({ opacity: v < 100 ? v : undefined });
          }}
          className="w-full accent-accent-500"
        />
      </div>
    </div>
  );
}

// ─── Helpers to check if section has values ───────────────────────────────────

function sectionHasValues(id: SectionId, s: LayoutElementStyle): boolean {
  switch (id) {
    case 'typography':
      return !!(
        s.fontSize ||
        s.fontWeight ||
        s.textColor ||
        s.textAlign ||
        s.textDecoration ||
        s.textTransform
      );
    case 'fill':
      return !!s.backgroundColor;
    case 'border':
      return !!(s.borderWidth || s.borderRadius);
    case 'spacing':
      return !!(
        s.paddingTop ||
        s.paddingRight ||
        s.paddingBottom ||
        s.paddingLeft ||
        s.marginTop ||
        s.marginBottom
      );
    case 'effects':
      return s.opacity !== undefined && s.opacity < 100;
    default:
      return false;
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export function StylePanel({ style: rawStyle, onChange }: StylePanelProps) {
  const style: LayoutElementStyle = rawStyle ?? {};
  const [open, setOpen] = useState<Set<SectionId>>(new Set(['fill', 'typography']));

  const toggle = (id: SectionId) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChange = (patch: Partial<LayoutElementStyle>) => {
    onChange({ ...style, ...patch });
  };

  const renderSection = (id: SectionId) => {
    switch (id) {
      case 'typography':
        return <TypographySection style={style} onChange={handleChange} />;
      case 'fill':
        return <FillSection style={style} onChange={handleChange} />;
      case 'border':
        return <BorderSection style={style} onChange={handleChange} />;
      case 'spacing':
        return <SpacingSection style={style} onChange={handleChange} />;
      case 'effects':
        return <EffectsSection style={style} onChange={handleChange} />;
    }
  };

  return (
    <div className="space-y-0.5">
      {SECTIONS.map(({ id, label, icon: Icon }) => {
        const isOpen = open.has(id);
        const hasValues = sectionHasValues(id, style);
        return (
          <div
            key={id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggle(id)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex-1 text-left">
                {label}
              </span>
              {hasValues && (
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
              )}
              <ChevronDown
                className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                {renderSection(id)}
              </div>
            )}
          </div>
        );
      })}

      {/* Resetuj style */}
      {rawStyle && Object.keys(rawStyle).length > 0 && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="w-full mt-2 py-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded border border-red-200 dark:border-red-800 transition-colors"
        >
          Resetuj wszystkie style
        </button>
      )}
    </div>
  );
}
